/**
 * Cache utility functions for managing Next.js cache tags and revalidation
 */

/**
 * Generate a consistent cache tag for a product
 */
export function cacheTag(
  type: 'product' | 'category' | 'homepage' | 'list',
  id?: string
): string {
  if (id) {
    return `${type}:${id}`;
  }
  return type;
}

/**
 * Generate product-specific cache tag
 */
export function productCacheTag(productId: string): string {
  return cacheTag('product', productId);
}

/**
 * Generate category-specific cache tag
 */
export function categoryCacheTag(categorySlug: string): string {
  return cacheTag('category', categorySlug);
}

/**
 * Revalidate product cache and related caches
 */
export async function revalidateProductCache(
  productId: string,
  slug?: string
): Promise<void> {
  const tags = [
    productCacheTag(productId),
    cacheTag('list'), // Invalidate product listing pages
  ];

  // Revalidate specific product page if slug is provided
  if (slug) {
    try {
      await fetch(
        `/api/revalidate?path=/product/${slug}&tags=${tags.join(',')}`,
        {
          method: 'GET',
        }
      );
    } catch (error) {
      console.error('Error revalidating product cache:', error);
    }
  } else {
    // Just invalidate tags
    try {
      await fetch(`/api/revalidate?tags=${tags.join(',')}`, {
        method: 'GET',
      });
    } catch (error) {
      console.error('Error revalidating product cache tags:', error);
    }
  }
}

/**
 * Revalidate category cache and related product list caches
 */
export async function revalidateCategoryCache(
  categorySlug: string
): Promise<void> {
  const tags = [
    categoryCacheTag(categorySlug),
    cacheTag('list'), // Invalidate product listing pages
  ];

  try {
    await fetch(`/api/revalidate?tags=${tags.join(',')}`, {
      method: 'GET',
    });
  } catch (error) {
    console.error('Error revalidating category cache:', error);
  }
}

/**
 * Revalidate homepage cache
 */
export async function revalidateHomepageCache(): Promise<void> {
  const tag = cacheTag('homepage');

  try {
    await fetch(`/api/revalidate?tags=${tag}&path=/`, {
      method: 'GET',
    });
  } catch (error) {
    console.error('Error revalidating homepage cache:', error);
  }
}
